import React, { useState, useEffect, useCallback } from "react";
import { Layout, message, Descriptions, Table, Button, Space, Popconfirm, Input, Row, Col, Card, Tag } from "antd";
import { CheckCircleOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useParams, useNavigate } from "react-router-dom";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import SupplierDetailsModal from "../../../components/common/SupplierDetailsModal";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { TextArea } = Input;

const GRNApprovalDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [grnDetails, setGrnDetails] = useState(null);
    const [grnItems, setGrnItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [comment, setComment] = useState("");
    const [supplierModalVisible, setSupplierModalVisible] = useState(false);

    const fetchGRNDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/inventory/grn/get.php?id=${id}`);
            if (response.data.status === "success") {
                setGrnDetails(response.data.master);
                setGrnItems(response.data.items || []);
            } else {
                message.error(response.data.message || "Failed to load GRN details.");
            }
        } catch (error) {
            message.error("Failed to fetch GRN details.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchGRNDetails();
    }, [fetchGRNDetails]);

    const handleApproval = async (status) => {
        setProcessing(true);
        try {
            const response = await api.post("/inventory/grn/approve.php", {
                grn_id: parseInt(id),
                status: status,
                approved_note: comment
            });

            if (response.data.status === "success") {
                message.success(response.data.message);
                navigate("/grn/approvals");
            } else {
                message.error(response.data.message || "Failed to process review.");
            }
        } catch (error) {
            message.error("Failed to process approval.");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Layout className="fluid-page">
                <Headerdiv />
                <Content className="fluid-content">
                    <div className="fluid-card" style={{ textAlign: 'center', padding: 50 }}>Loading...</div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    if (!grnDetails) {
        return (
            <Layout className="fluid-page">
                <Headerdiv />
                <Content className="fluid-content">
                    <div className="fluid-card" style={{ padding: 24, textAlign: 'center' }}>Goods Received Note not found</div>
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    const renderStatusTag = (status) => {
        return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />
            
            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <AppBreadcrumb items={[
                            { label: 'Inventory', link: '/inventory-items' },
                            { label: 'GRN Approvals', link: '/grn/approvals' },
                            { label: `Review: ${grnDetails.grn_ref}` },
                        ]} />
                        <h1 className="fluid-page-title">
                            <CheckCircleOutlined />Approve GRN: {grnDetails.grn_ref}
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Verify received items and commit stock quantities</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} lg={12}>
                        <Card className="fluid-card" title="GRN Overview" style={{ height: '100%' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="GRN Reference"><strong>{grnDetails.grn_ref}</strong></Descriptions.Item>
                                <Descriptions.Item label="GRN Create Date">{grnDetails.created_dt ? dayjs(grnDetails.created_dt).format('YYYY-MM-DD HH:mm') : (grnDetails.grn_date ? dayjs(grnDetails.grn_date).format('YYYY-MM-DD') : '-')}</Descriptions.Item>
                                <Descriptions.Item label="Created By">{grnDetails.creator_name || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Status">{renderStatusTag(grnDetails.grn_status)}</Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Card className="fluid-card" title="PO / Supplier Details" style={{ height: '100%' }}>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Supplier Ref">{grnDetails.sup_ref ? <Tag color="cyan">{grnDetails.sup_ref}</Tag> : "-"}</Descriptions.Item>
                                <Descriptions.Item label="Supplier Ref Date">{grnDetails.sup_ref_date ? dayjs(grnDetails.sup_ref_date).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                                <Descriptions.Item label="PO Associated">
                                    {grnDetails.po_ref ? <Tag color="blue">{grnDetails.po_ref}</Tag> : "-"}
                                </Descriptions.Item>
                                {grnDetails.pr_note && (
                                    <Descriptions.Item label="PR Note">{grnDetails.pr_note}</Descriptions.Item>
                                )}
                            </Descriptions>

                            {grnDetails.supplier_name && (
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--fluid-primary)' }}>Supplier</div>
                                        <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {grnDetails.supplier_name}
                                        </div>
                                    </div>
                                    <Button icon={<EyeOutlined />} onClick={() => setSupplierModalVisible(true)}>
                                        View Supplier
                                    </Button>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>

                <Card className="fluid-card" title="Received Items Verification" style={{ marginTop: 16 }}>
                    <Table
                        dataSource={grnItems}
                        rowKey="grnitem_id"
                        pagination={false}
                        bordered
                        size="middle"
                        columns={[
                            { title: "Item Code", dataIndex: "inv_code", key: "inv_code", render: (val) => val || "-" },
                            {
                                title: "Item Description",
                                key: "inv_desc",
                                render: (_, record) => (
                                    <div>
                                        <strong>{record.inv_desc}</strong>
                                        {record.pr_item_note && (
                                            <div style={{ fontSize: "12px", color: "#faad14", marginTop: 2, fontWeight: "500" }}>
                                                PR Note: {record.pr_item_note}
                                            </div>
                                        )}
                                        {record.grnitem_note && (
                                            <div style={{ fontSize: "12px", color: "#1890ff", marginTop: 2, fontWeight: "500" }}>
                                                GRN Note: {record.grnitem_note}
                                            </div>
                                        )}
                                    </div>
                                )
                            },
                            { title: "Received Qty", dataIndex: "grn_qty", key: "grn_qty", align: "right", render: (qty) => <strong>{Number(qty).toFixed(2)}</strong> },
                            { title: "Unit Cost", dataIndex: "cost_price", key: "cost_price", align: "right", render: (val) => Number(val).toFixed(2) },
                            { title: "Sub Total Cost", dataIndex: "item_subtotal", key: "item_subtotal", align: "right", render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> }
                        ]}
                    />

                    <div style={{ marginTop: 24, textAlign: "right" }}>
                        <Descriptions column={1} size="small" bordered style={{ width: 340, display: "inline-block" }}>
                            <Descriptions.Item label="Sub Total">LKR {parseFloat(grnDetails.grn_sub_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Tax">LKR {parseFloat(grnDetails.grn_total_tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Discount">
                                <span style={{ color: '#cf1322' }}>
                                    - LKR {parseFloat(grnDetails.grn_total_discount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Grand Total Cost">
                                <strong style={{ fontSize: "16px", color: "#3f8600" }}>
                                    LKR {parseFloat(grnDetails.grn_final_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </strong>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </Card>

                {grnDetails.grn_status === 'Pending Approval' ? (
                    <Card className="fluid-card" title="Approval Action" style={{ marginTop: 16 }}>
                        <Row gutter={24}>
                            <Col xs={24} md={16}>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Review Remarks / Comments (Optional)</label>
                                    <TextArea 
                                        rows={4} 
                                        placeholder="Add notes explaining your decision..." 
                                        value={comment} 
                                        onChange={(e) => setComment(e.target.value)} 
                                    />
                                </div>
                            </Col>
                            <Col xs={24} md={8} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Popconfirm title="Approve Goods Received Note?" description="This will commit stock quantities and update inventory levels. Proceed?" onConfirm={() => handleApproval('Approved')} okText="Yes" cancelText="No">
                                        <Button type="primary" block icon={<CheckOutlined />} className="fluid-btn-primary" loading={processing}>
                                            Approve & Commit Stock
                                        </Button>
                                    </Popconfirm>
                                    <Popconfirm title="Reject Goods Received Note?" description="Are you sure you want to reject this GRN?" onConfirm={() => handleApproval('Rejected')} okText="Yes" cancelText="No">
                                        <Button danger block icon={<CloseOutlined />} loading={processing}>
                                            Reject GRN
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                ) : (
                    <Card className="fluid-card" title="Audit Trail" style={{ marginTop: 16 }}>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Reviewed By">{grnDetails.approver_name || '-'}</Descriptions.Item>
                            <Descriptions.Item label="Reviewed Date">{grnDetails.approved_dt ? dayjs(grnDetails.approved_dt).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                            <Descriptions.Item label="Review Remarks" span={2}>{grnDetails.approved_note || '-'}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                )}
            </Content>

            <SupplierDetailsModal
                visible={supplierModalVisible}
                onCancel={() => setSupplierModalVisible(false)}
                supplier={grnDetails}
            />

            <Footerdiv />
        </Layout>
    );
};

export default GRNApprovalDetail;
